import { generateUID, getColorProperty, getNumberProperty, getStringProperty } from "@/canv/utils";
import type {
    Direction,
    MindMapWidgetNodeFixState,
    MindMapWidgetNodeStateByFixState,
} from "@/elements/mindMap/types";
import { TextWidgetV2 } from "@/elements/text/textV2";
import { generateKeyBetween } from "@/helpers/fractional-indexing";
import { t } from "@/i18n";

export class MindMapWidgetNode {
    constructor(
        startX: number,
        startY: number,
        parentNodeUID = "",
        direction: Direction = "direct",
    ) {
        this.UID = generateUID();
        this.parentNodeUID = parentNodeUID;
        this.direction = direction;
        this.level = generateKeyBetween(null, null);

        this.textPart = new TextWidgetV2();
        this.textPart.menuTextSize(27);
        this.setupTextPart();

        this.startX = startX;
        this.startY = startY;
        this.updateDims();
    }

    private setupTextPart() {
        this.textPart.setKeepMinWidth(false);
        this.textPart.setGreyText(t("editText.placeholder"));

        this.textPart.setEditingAutosaveHandler(false);
    }

    moveNode(x: number, y: number) {
        this.startX += x;
        this.startY += y;
        this.updateDims();
    }

    updateDims() {
        const verticalPadding = 5;
        const horizontalPadding = 5;

        const isTextEditing = this.textPart.getIsEditing();
        const lineHeight = this.textPart.lineHeight;

        this.textPart.startX = this.startX + horizontalPadding;
        this.textPart.startY = this.startY + verticalPadding + lineHeight;

        if (!isTextEditing) {
            this.textPart.updateDims();
        }

        this.height = this.textPart.height + 2 * verticalPadding;
        this.width = this.textPart.width + 2 * horizontalPadding;
    }

    async TryAction(type) {
        await this.textPart.TryAction(type);
        this.updateDims();
    }
    // draw

    draw() {
        this.textPart.draw();
    }

    getState<F extends MindMapWidgetNodeFixState>(fixType: F): MindMapWidgetNodeStateByFixState<F> {
        const state = {};

        switch (fixType) {
            case "props":
                state["UID"] = this.UID;
                state["startX"] = this.startX; // нужно при перемещении rootMove
                state["startY"] = this.startY;
                state["color"] = this.color;
                state["textPart"] = this.textPart.getState("full");
                break;
            case "full":
                state["UID"] = this.UID;
                state["startX"] = this.startX;
                state["startY"] = this.startY;
                state["parentNodeUID"] = this.parentNodeUID;
                state["direction"] = this.direction;
                state["level"] = this.level;
                state["color"] = this.color;
                state["textPart"] = this.textPart.getState("full");
        }

        return state as MindMapWidgetNodeStateByFixState<F>;
    }

    loadState<F extends MindMapWidgetNodeFixState>(
        stateObj: MindMapWidgetNodeStateByFixState<F>,
        fixType: F,
    ) {
        switch (fixType) {
            case "props": {
                // нельзя использовать full для widget menu, так как он запоминает parentNodeUID, direction...
                this.UID = getStringProperty("UID", stateObj);
                this.startX = getNumberProperty("startX", stateObj);
                this.startY = getNumberProperty("startY", stateObj);
                this.color = getColorProperty("color", stateObj);
                this.setupTextPart();
                this.textPart.loadState(stateObj.textPart, "full");
                break;
            }
            case "full": {
                this.UID = getStringProperty("UID", stateObj);
                this.startX = getNumberProperty("startX", stateObj);
                this.startY = getNumberProperty("startY", stateObj);
                this.parentNodeUID = getStringProperty("parentNodeUID", stateObj);
                this.direction = getStringProperty("direction", stateObj);
                this.level = getStringProperty("level", stateObj);
                this.color = getColorProperty("color", stateObj);

                this.setupTextPart();
                this.textPart.loadState(stateObj["textPart"], "full");
                break;
            }
        }

        this.updateDims();
    }

    UID = "";
    startX = 0;
    startY = 0;
    width = 0;
    height = 0;
    color = "#B3B3B3";

    textPart: TextWidgetV2;

    parentNodeUID = ""; // UID
    direction: Direction | "" = "direct"; // direct | reverse
    level = "";
}
